// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { formatNumber } from 'devextreme/localization';
import notify from 'devextreme/ui/notify';
import { DxPopupComponent } from 'devextreme-angular';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Constant imports
import { Constants } from '../../../../_constants/constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

// Service imports
import { DocumentService } from '../../../../shared-document-module/_services/document.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { APBillService } from '../../_services/accounts-payable/ap-bill.service';
import { APCreditApplicationsService } from '../../_services/accounts-payable/ap-credit-applications.service';
import { CBSharedService } from '../../_services/shared-services/cb-shared-service';
import { UserService } from '../../../../_services/data-services/user.service';

// Model imports
import { DocumentResultDto } from '../../../../shared-document-module/_models/document-result-dto';
import { APBillParameterDto } from '../../_models/accounts-payable/ap-bill/ap-bill-parameter-dto';
import { APBillResultDto } from '../../_models/accounts-payable/ap-bill/ap-bill-result-dto';
import { APBillReverseParametersDto } from '../../_models/accounts-payable/ap-bill/ap-bill-reverse-parameter-dto';
import { APCreditApplicationParameterDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-parameter-dto';
import { APCreditApplicationResultDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-result-dto';
import { APServiceChargeEditorParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-parameter-dto';
import { APServiceChargeEditorResultDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-result-dto';
import { APServiceChargeEditorSubcategoriesParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-subcategories-parameter-dto';
import { APServiceChargeRawDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-raw-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { ReverseEditorResultDto } from '../../../../../app/shared-module/_models/reverse-editor-result-dto';

@Component({
    selector: 'app-ap-bill-detail',
    templateUrl: './ap-bill-detail.component.html',
    styleUrls: ['./ap-bill-detail.component.scss']
})
export class APBillDetailComponent implements OnInit {
    @ViewChild('workflowEditorPopup', { static: false }) workflowEditorPopup: DxPopupComponent;

    @Input() visible: boolean;
    @Input() apBillDetailParam: APBillParameterDto;
    @Input() isCorporateCirabook: boolean;
    @Output() onAPBillDetailClose = new EventEmitter<boolean>();
    popUpTitle: string;
    defaultLabelLocation: string;

    apBillData: APBillResultDto;
    loadingVisible: boolean;
    hasOtherDimensions: boolean;
    currencyFormat: any;
    dateFormat: any;
    dateTimeFormat = StringFormatConstants.dateTimeFormat;
    historyButtonOptions: any;

    showTransactionLog = false;
    transactionLogTitle: string;
    transactionLogs: Array<TransactionLogResultDto> = [];

    showReclassify = false;
    showWorkflowDetail = false;
    showWorkFlowEditor = false;
    workflowLink: string;

    showReverseBill = false;
    reverseEditorData = new ReverseEditorResultDto();
    reverseAPTitle: string;

    showCreditApplication = false;
    creditApplicationData: APCreditApplicationResultDto[] = [];
    creditApplicationTitle: string;
    transactionType = 'B';

    showAddServiceChargeEditor = false;
    addServiceChargeTitle: string;
    serviceChargeParam: APServiceChargeEditorParameterDto;
    serviceChargeEditorData: APServiceChargeEditorResultDto;
    subCategories: GLSubcategorySelectorResultDto[] = [];

    isInternal: boolean;

    iconClose = IconConstants.CLOSE;
    iconRandom = IconConstants.RANDOM;
    iconEdit = IconConstants.EDIT;
    iconAdd = IconConstants.ADD;
    iconTrash = IconConstants.DELETE;
    iconReverse = IconConstants.REVERSE;

    constructor(
        private readonly apBillService: APBillService,
        private readonly precisionService: PrecisionService,
        private readonly serverErrorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly documentService: DocumentService,
        private readonly cbSharedService: CBSharedService,
        private readonly apCreditApplicationService: APCreditApplicationsService,
        private readonly userService: UserService
    ) {
        this.dateFormat = StringFormatConstants.dateFormat;
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.onTransactionLogClick = this.onTransactionLogClick.bind(this);
    }

    ngOnInit() {
        this.isInternal = this.userService.isInternal();
        this.defaultLabelLocation = Constants.defaultFormLabelLocation;
        this.historyButtonOptions = {
            icon: IconConstants.HISTORY,
            hint: 'Transaction History',
            onClick: this.onTransactionLogClick
        };
    }

    onHidden(): void {
        this.apBillDetailParam = null;
        this.popUpTitle = '';
        this.visible = false;
        this.onAPBillDetailClose.emit(false);
    }

    onShown(): void {
        if (Utils.notNullAndDefined(this.apBillDetailParam)) {
            this.popUpTitle = 'AP Bill ' + this.apBillDetailParam.billId;
            this.getAPBill();
        }
    }

    getFileIconByExtension(file: DocumentResultDto): string {
        const fileName = file.fileName;
        return this.helperService.getFileIconByExtension(fileName);
    }

    onTransactionLogClick(): void {
        this.showTransactionLog = true;
        this.transactionLogTitle = `Transaction Log of #${this.apBillDetailParam.billId}`;
        this.getTransactionLogs();
    }

    onTransactionLogClose(): void {
        this.showTransactionLog = false;
    }

    onDownloadClick(event: any): void {
        this.downloadFile(event.downloadUrl, event.fileName);
    }

    onReclassifyClick(): void {
        this.showReclassify = true;
    }

    onViewerClose(event: any): void {
        this.showReclassify = false;
        this.showWorkflowDetail = false;
        if (Utils.isTrue(event)) {
            this.getAPBill();
        }
    }

    onWorkflowIdClick(): void {
        this.showWorkflowDetail = true;
    }

    onReverseBillClick(): void {
        this.getAPBillReverseEditor();
    }

    onReverseBill(event: any): void {
        const baseParam: APBillReverseParametersDto = new APBillReverseParametersDto();
        baseParam.billId = this.apBillData.billId;
        baseParam.managementCompanyId = this.apBillData.managementCompanyId;
        baseParam.communityId = this.apBillData.communityId;
        baseParam.workflowId = this.apBillData.wfJobId;
        baseParam.reverseDate = event.reverseDate;

        this.reverseAPBill(baseParam);
    }

    onReverseBillClose(): void {
        this.showReverseBill = false;
    }

    currencyFormatter(value: number): string {
        return formatNumber(value, this.currencyFormat);
    }

    showAPCreditApplication(): void {
        const param: APCreditApplicationParameterDto = new APCreditApplicationParameterDto();
        param.communityId = this.apBillDetailParam.communityId;
        param.managementCompanyId = this.apBillDetailParam.managementCompanyId;
        param.creditApplicationId = null;
        if (this.apBillData.amount < 0) {
            param.sourceId = this.apBillData.billId;
            param.sourceType = 'B';
        } else {
            param.targetId = this.apBillData.billId;
            param.targetType = 'B';
        }

        param.clearCache = true;

        this.loadingVisible = true;
        this.apCreditApplicationService.getAPCreditApplications(param).subscribe(
            (result: APCreditApplicationResultDto[]) => {
                this.creditApplicationData = result;
                this.creditApplicationTitle = `Payment Application for Bill Number ${this.apBillDetailParam.billId}`;
                this.showCreditApplication = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    closeCreditApplication(refresh: boolean): void {
        this.showCreditApplication = false;
        if (Utils.isTrue(refresh)) {
            this.getAPBill();
        }
    }

    onAddServiceChargeClick(): void {
        this.addServiceChargeTitle = 'Add Service Charge';
        this.populateServiceChargeEditor();
    }

    getSubcategories(serviceCharge: APServiceChargeRawDto): void {
        if (Utils.notNullAndDefined(serviceCharge)) {
            this.populateServiceChargeSubcategories(serviceCharge);
        }
    }

    addAPServiceCharge(param: APServiceChargeRawDto): void {
        this.addServiceCharge(param);
    }

    closeServiceChargePopUp(): void {
        this.showAddServiceChargeEditor = false;
    }

    onVendorNameClick(detail: APBillResultDto): void {
        if (Utils.notNullAndDefined(detail.vendorId)) {
            const contextType = 'Community';
            const contextId = detail.communityId;
            const vendorCode = this.apBillDetailParam.vendorCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;
            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    onUpdateBillClick(): void {
        this.workflowLink = `workflow/workflow-editor?workflowId=${this.apBillData.wfJobId}&workflowStatus=${this.apBillData.wfStatus}&communityId=${this.apBillData.communityId}&isPopupMode=false`;
        this.showWorkFlowEditor = true;
    }

    onDeleteBillClick(): void {
        this.deleteApBill();
    }

    onWorkflowEditorShown(): void {
        setTimeout(() => {
            (document.getElementById('frameElement') as any).onWorkflowEditorFinish =
                this.onWorkflowEditorClose.bind(this);
        }, 3000);
    }

    onWorkflowEditorClose(): void {
        this.workflowEditorPopup.instance.hide();
        this.showWorkFlowEditor = false;
        this.workflowLink = null;
    }

    private deleteApBill(): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            'Are you sure you want to delete this Bill ?'
        );

        dialog.show().then((answer: boolean) => {
            if (Utils.isTrue(answer)) {
                this.loadingVisible = true;
                this.apBillService.deleteAPBill(this.apBillDetailParam).subscribe(
                    () => {
                        const message = `AP Bill #${this.apBillDetailParam.billId} successfully deleted.`;
                        notify(message, 'Success', MessageInterval.successMsgInterval);
                        this.onAPBillDetailClose.emit(true);
                        this.loadingVisible = false;
                    },
                    (error) => {
                        this.serverErrorService.displayError(error);
                        this.loadingVisible = false;
                    }
                );
            }
        });
    }

    private getAPBill(): void {
        const param: APBillParameterDto = new APBillParameterDto();
        param.billId = this.apBillDetailParam.billId;
        param.communityId = this.apBillDetailParam.communityId;
        param.managementCompanyId = this.apBillDetailParam.managementCompanyId;
        param.clearCache = true;

        this.loadingVisible = true;
        this.apBillService.getAPBillDetails(param).subscribe(
            (result: APBillResultDto) => {
                this.apBillData = result;

                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.visible = false;
                this.onAPBillDetailClose.emit(false);
                this.serverErrorService.displayError(error);
            }
        );
    }

    private downloadFile(fileURL: string, fileName: string): void {
        this.loadingVisible = true;
        this.helperService
            .downloadFileUsingServiceMethod(this.documentService.downloadFile(fileURL), fileName)
            .subscribe(
                () => {
                    this.loadingVisible = false;
                },
                (error) => {
                    this.serverErrorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
    }

    private getTransactionLogs(): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = this.apBillDetailParam.billId;
        baseParam.clearCache = true;
        this.loadingVisible = true;
        this.apBillService.getAPBillTransactionLog(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogs = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private reverseAPBill(baseParams: APBillReverseParametersDto): void {
        this.loadingVisible = true;
        this.apBillService.reverseAPBill(baseParams).subscribe(
            () => {
                this.loadingVisible = false;
                notify(
                    `AP Bill #${this.apBillData.billId} has been successfully reversed.`,
                    'Success',
                    MessageInterval.successMsgInterval
                );
                this.showReverseBill = false;
                this.getAPBill();
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    private getAPBillReverseEditor(): void {
        const baseParam: APBillParameterDto = new APBillParameterDto();
        baseParam.communityId = this.apBillDetailParam.communityId;
        baseParam.managementCompanyId = this.apBillDetailParam.managementCompanyId;
        baseParam.billId = this.apBillDetailParam.billId;

        this.apBillService.getAPBillReverseEditor(baseParam).subscribe(
            (result: ReverseEditorResultDto) => {
                this.reverseEditorData = result;

                this.reverseAPTitle = 'Reverse Bill';
                this.showReverseBill = true;
            },
            (error) => {
                this.serverErrorService.displayError(error);
            }
        );
    }

    private populateServiceChargeEditor(): void {
        this.serviceChargeParam = new APServiceChargeEditorParameterDto();
        this.serviceChargeParam.apBillId = this.apBillData.billId;
        this.serviceChargeParam.communityId = this.apBillData.communityId;
        this.serviceChargeParam.managementCompanyId = this.apBillData.managementCompanyId;

        this.apBillService.getServiceChargeEditor(this.serviceChargeParam).subscribe(
            (result: APServiceChargeEditorResultDto) => {
                this.serviceChargeEditorData = result;
                this.showAddServiceChargeEditor = true;
            },
            (error) => {
                this.serverErrorService.displayError(error);
            }
        );
    }

    private populateServiceChargeSubcategories(serviceCharge: APServiceChargeRawDto): void {
        if (Utils.notNullAndDefined(this.serviceChargeParam)) {
            const param: APServiceChargeEditorSubcategoriesParameterDto =
                new APServiceChargeEditorSubcategoriesParameterDto();
            param.glAccountNumber = serviceCharge.glAccountNumber;
            param.communityId = this.serviceChargeParam.communityId;
            param.managementCompanyId = this.serviceChargeParam.managementCompanyId;
            param.clearCache = true;
            this.apBillService.getServiceChargeEditorSubcategories(param).subscribe(
                (result: GLSubcategorySelectorResultDto[]) => {
                    this.subCategories = result;
                    if (Utils.notNullAndDefined(serviceCharge.subCategoryCode)) {
                        const selectedSubCategory = this.subCategories.find((d) =>
                            Utils.equalsIgnoreCase(d.code, serviceCharge.subCategoryCode)
                        );
                        if (Utils.notNullAndDefined(selectedSubCategory)) {
                            serviceCharge.subCategoryCode = selectedSubCategory.code;
                            setTimeout(() => {
                                this.serviceChargeEditorData.serviceCharge = serviceCharge;
                            });
                        }
                    }
                },
                (error) => {
                    this.serverErrorService.displayError(error);
                }
            );
        }
    }

    private addServiceCharge(param: APServiceChargeRawDto): void {
        this.loadingVisible = true;
        this.apBillService.addServiceCharge(param).subscribe(
            () => {
                if (Utils.isTrue(param.payBill)) {
                    notify(
                        `Successfully added and paid including additional charge to Bill ${param.apBillId}`,
                        'success',
                        MessageInterval.successMsgInterval
                    );
                } else {
                    notify(
                        `Successfully added and paid service charge to Bill ${param.apBillId}`,
                        'success',
                        MessageInterval.successMsgInterval
                    );
                }
                this.showAddServiceChargeEditor = false;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
